/**
 * Corporate clients (EAP): companies that pay for their employees' care,
 * and the patients they cover. What goes to a company is counts only.
 */
import { apiGet, apiPost, apiPut } from "./client";

export type BillingModel = "usage" | "prepaid" | "retainer" | "workshop";
export type ContractState = "active" | "ending" | "not_started" | "expired" | "none";

export const BILLING_MODELS: { value: BillingModel; label: string; hint: string }[] = [
  { value: "usage", label: "Monthly invoice for sessions held", hint: "Bill the company at month end for what was used." },
  { value: "prepaid", label: "Prepaid bundle", hint: "The company buys sessions upfront; each visit uses one." },
  { value: "retainer", label: "Fixed retainer", hint: "A set fee each month or year, however many sessions." },
  { value: "workshop", label: "Workshops and group sessions", hint: "A flat fee for each on-site session or training." },
];

export interface CorporateClient {
  id: string;
  name: string;
  code: string;
  industry?: string | null;
  hr_contact_name?: string | null;
  hr_contact_email?: string | null;
  hr_contact_phone?: string | null;
  billing_name?: string | null;
  billing_email?: string | null;
  billing_phone?: string | null;
  tin?: string | null;
  address?: string | null;
  contract_start?: string | null;
  contract_end?: string | null;
  sessions_per_year?: number | null;
  dependants_covered: boolean;
  billing_models: BillingModel[];
  retainer_amount?: number | null;
  retainer_period: "monthly" | "yearly";
  retainer_per_member: boolean;
  notes?: string | null;
  booking_code?: string | null;
  self_booking: boolean;
  status: "active" | "archived";
  created_at: string;
  // Numbers for the current contract year
  active_members: number;
  ended_members: number;
  sessions_used: number;
  sessions_booked: number;
  members_at_limit: number;
  period: { from: string; to: string };
  contract_state: ContractState;
}

export interface CorporateClientInput {
  name: string;
  code?: string;
  industry?: string | null;
  hr_contact_name?: string | null;
  hr_contact_email?: string | null;
  hr_contact_phone?: string | null;
  billing_name?: string | null;
  billing_email?: string | null;
  billing_phone?: string | null;
  tin?: string | null;
  address?: string | null;
  contract_start?: string | null;
  contract_end?: string | null;
  sessions_per_year?: number | null;
  dependants_covered: boolean;
  billing_models: BillingModel[];
  retainer_amount?: number | null;
  retainer_period?: "monthly" | "yearly";
  retainer_per_member?: boolean;
  notes?: string | null;
}

export type Relationship = "employee" | "dependant";

export interface CorporateMember {
  id: string;
  corporate_client_id: string;
  patient_id: string;
  patient_name: string;
  employee_id?: string | null;
  department?: string | null;
  relationship: Relationship;
  covered_from: string;
  covered_to?: string | null;
  status: "active" | "ended";
  ended_reason?: string | null;
  used: number;
  booked: number;
  allowance?: number | null;
  left?: number | null;
}

export interface MemberInput {
  patient_id: string;
  employee_id?: string | null;
  department?: string | null;
  relationship: Relationship;
  covered_from?: string | null;
}

export interface ImportRow {
  mrn?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
  gender?: string;
  phone?: string;
  email?: string;
  employee_id?: string;
  department?: string;
  relationship?: string;
}

export interface ImportSummary {
  added: number;
  created: number;
  skipped: number;
  results: { row: number; outcome: "added" | "created" | "skipped"; message?: string; patient_id?: string }[];
}

export interface Coverage {
  covered: boolean;
  client?: CorporateClient;
  member?: CorporateMember;
  period?: { from: string; to: string };
  used: number;
  booked: number;
  allowance?: number | null;
  left?: number | null;
  contract_state?: ContractState;
  warning?: string;
}

export interface CoverageEntry {
  patient_id: string;
  corporate_client_id: string;
  name: string;
  code: string;
}

const base = "/corporate-clients";

export const corporateApi = {
  list: (params?: { status?: "active" | "archived" | "all"; search?: string }) =>
    apiGet<CorporateClient[]>(base, { params }),
  get: (id: string) => apiGet<CorporateClient>(`${base}/${id}`),
  create: (body: CorporateClientInput) => apiPost<CorporateClient>(base, body),
  update: (id: string, body: CorporateClientInput) => apiPut<CorporateClient>(`${base}/${id}`, body),
  archive: (id: string, reason: string) => apiPost<CorporateClient>(`${base}/${id}/archive`, { reason }),
  reactivate: (id: string) => apiPost<CorporateClient>(`${base}/${id}/reactivate`),

  members: (id: string, status?: "active" | "ended") =>
    apiGet<CorporateMember[]>(`${base}/${id}/members`, { params: status ? { status } : undefined }),
  addMember: (id: string, body: MemberInput) => apiPost<CorporateMember>(`${base}/${id}/members`, body),
  updateMember: (id: string, memberId: string, body: MemberInput) =>
    apiPut<CorporateMember>(`${base}/${id}/members/${memberId}`, body),
  endMember: (id: string, memberId: string, body: { reason: string; on?: string }) =>
    apiPost<CorporateMember>(`${base}/${id}/members/${memberId}/end`, body),
  importMembers: (id: string, rows: ImportRow[], coveredFrom?: string) =>
    apiPost<ImportSummary>(`${base}/${id}/members/import`, { rows, covered_from: coveredFrom || null }),

  coverage: (patientId: string) => apiGet<Coverage>(`${base}/coverage/${patientId}`),
  setBookingCode: (id: string, body: { enabled: boolean; regenerate?: boolean }) =>
    apiPost<CorporateClient>(`${base}/${id}/booking-code`, body),
  report: (id: string, from: string, to: string) => apiGet<UsageReport>(`${base}/${id}/report`, { params: { from, to } }),
  sendReport: (id: string, from: string, to: string) => apiPost<{ sent_to: string }>(`${base}/${id}/report/send`, { from, to }),
  coverageMap: () => apiGet<CoverageEntry[]>(`${base}/coverage`),
};

/** A count the server may hide (1–4 people) so no one can be identified. */
export interface HiddenCount {
  value?: number;
  hidden: boolean;
  text: string;
}

export interface ReportRow {
  label: string;
  sessions: HiddenCount;
  people: HiddenCount;
}

export interface UsageReport {
  company: string;
  from: string;
  to: string;
  members: number;
  people_used: HiddenCount;
  usage_rate: string;
  sessions: HiddenCount;
  missed: HiddenCount;
  by_month: ReportRow[];
  by_type: ReportRow[];
  by_department: ReportRow[];
  workshops: number;
  workshop_attendees: number;
  note: string;
}

export const CONTRACT_STATE_LABEL: Record<ContractState, string> = {
  active: "Contract active",
  ending: "Ends within 30 days",
  not_started: "Starts soon",
  expired: "Contract ended",
  none: "No contract dates",
};
